import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import {
  dateConverter,
  DateInputFormatterPipe,
  ValidationClassesDirective,
} from '@innogy/utils-deprecated';
import type { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-angular';
import { FocusAction, FormState, NgrxFormsModule } from 'ngrx-forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DatePipe } from '@angular/common';
import { TrackValueChangeDirective } from '@analytics/events';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { CommonUiUtilityComponentsModule } from '@legacy/common-ui-utility-components';

import { toDateString, toNgbDate } from './datepicker.helper';
import {
  CustomAdapter,
  CustomDateParserFormatter,
} from './datepicker.adapters';

@Component({
  selector: 'wl-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: [
    './datepicker.component.ed.scss',
    './datepicker.component.essent.scss',
  ],
  providers: [
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    DateInputFormatterPipe,
  ],
  standalone: true,
  imports: [
    TrackValueChangeDirective,
    NgbInputDatepicker,
    NgrxFormsModule,
    AutomationIdDirective,
    ValidationClassesDirective,
    AutomationIdDirective,
    CommonUiUtilityComponentsModule,
    FaIconComponent,
    DatePipe,
  ],
})
export class DatepickerComponent {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() labelFloating = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() autocomplete = 'off';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() placeholder = '';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() label = '';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set startValidDate(date: NgbDateStruct | Date | string) {
    if (date) {
      this.minDate = toNgbDate(date);
    }
  }
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set endValidDate(date: NgbDateStruct | Date | string) {
    if (date) {
      this.maxDate = toNgbDate(date);
    }
  }
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() fieldName?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() formName?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() formsControl?: FormState<string>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering?: ComponentRendering;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() toolName?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() stepName?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() step?: number;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() automationId?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() tooltip = '';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() skipDirtyCheck = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() navigation: 'select' | 'arrows' | 'none' = 'arrows';
  @ViewChild('dateInput')
  dateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('datePicker')
  datePicker!: NgbInputDatepicker;
  @ViewChild('trackingWrapper')
  trackingWrapperRef!: ElementRef<HTMLDivElement>;

  public value = '';
  public dateValueConverter = dateConverter;
  public maxDate?: NgbDateStruct;
  public minDate?: NgbDateStruct;

  constructor(
    private readonly store$: Store<any>,
    private readonly formatDateInputPipe: DateInputFormatterPipe,
    private readonly ref: ChangeDetectorRef
  ) {}

  onValueChange(value: string | undefined) {
    const formattedValue = this.formatDateInputPipe.transform(value);
    /**
     * There could be a scenario where the user input's a character which is removed again during formatting.
     * This will cause the old value and new value to be the same, in this case the changeDetection assumes
     * there are no changes in the input and does not set the value. This will have the side effect of the input
     * still showing the added character, while the value is different.
     *
     * example scenario:
     *   userInput = "01-0a"
     *   value before & after last keystroke = "01-0" (the "a" is removed in formatting)
     *
     * The following check will "reset the value" and run the changeDetector so it can update the input
     * with the new value.
     */
    if (formattedValue === this.value) {
      this.value = '';
      this.ref.detectChanges();
    }
    this.value = formattedValue;
  }

  openDatepicker(datepicker: NgbInputDatepicker) {
    if (this.formsControl != null) {
      this.store$.dispatch(new FocusAction(this.formsControl.id));
    }
    datepicker.open();
    this.dispatchNativeEvent('focus');
    setTimeout(() => this.dateInput.nativeElement.focus());
  }

  private dispatchNativeEvent(eventName: string) {
    this.trackingWrapperRef.nativeElement.dispatchEvent(new Event(eventName));
  }

  selectDate(date: NgbDateStruct | undefined) {
    this.value = date ? toDateString(date) : '';
    this.dispatchNativeEvent('change');
  }
}
